import React, { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableRow, Option, Modal, ModalType } from '@code/innovance-react-ui-kit';

import { UploadType, ModelError } from 'src/front/api/generated';
import { Document } from 'src/front/api/apiInstance';

import { TableContainer, TableWrapper } from 'src/front/components/FormStyles';

import UploadDocumentModal, {
  UploadDocumentFormData,
} from 'src/front/components/document/UploadDocumentModal';
import preview from 'src/front/common/utils/previewUtils';

const documentTableHeaders = [
  'general_documentType_label',
  'general_uploadedDate_text',
  'general_note_label',
];

interface Props {
  documents: UploadDocumentFormData[];
  setDocuments: (documents: UploadDocumentFormData[]) => void;
  defaultExplanation?: string;
  documentTypes: Option[];
  isLoading: boolean;
  uploadType?: UploadType;
  customerId?: string;
}

const UploadDocument: FC<Props> = ({
  documents,
  setDocuments,
  defaultExplanation,
  isLoading,
  documentTypes,
  uploadType = UploadType.DOCUMENTUPLOAD,
  customerId,
}) => {
  const { t } = useTranslation();

  const [showIdentityModal, setShowIdentityModal] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const [selectedDocument, setSelectedDocument] = useState<TableRow>();
  const [documentFormValues, setDocumentFormValues] = useState<UploadDocumentFormData>();
  const [uploadError, setUploadError] = useState<ModelError>();

  useEffect(() => {
    if (!isLoading) {
      setShowIdentityModal(documents.length < 1);
    }
  }, [documents.length, isLoading]);

  const closeIdentityModal = (): void => {
    setUploadError(undefined);
    setShowIdentityModal(false);
  };

  const handleIdentitySubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    setUploadError(undefined);

    try {
      let newDocuments;
      if (!formData.documentId && formData.documentType) {
        const { data } = await Document.uploadDocuments(
          formData.files && formData.files[0],
          formData.documentType.value,
          uploadType,
          formData.explanation,
          customerId
        );
        if (data.result === 'OK') {
          newDocuments = [
            {
              documentType: (documentTypes &&
                documentTypes.find((type) => type.value === data.documentType)) || {
                value: data.documentType,
                label: data.documentType,
              },
              explanation: data.explanation,
              isOldDocument: data.isOldDocument,
              documentId: data.documentId,
              createDate: data.createDate,
              filePath: formData.filePath,
              documentName: data.documentName,
            },
            ...documents,
          ];
        }
      } else if (formData.documentType) {
        const { data } = await Document.updateDocument({
          documentId: formData.documentId,
          explanation: formData.explanation,
          documentType: formData.documentType.value,
          documentName: formData.documentName,
          uploadType,
        });
        if (data.result === 'OK') {
          newDocuments = documents.map((doc) =>
            doc.documentId === formData.documentId
              ? {
                  documentType: (documentTypes &&
                    documentTypes.find((type) => type.value === data.documentType)) || {
                    value: data.documentType,
                    label: data.documentType,
                  },
                  explanation: data.explanation,
                  isOldDocument: data.isOldDocument,
                  documentId: data.documentId,
                  createDate: data.createDate,
                  filePath: formData.filePath,
                  documentName: data.documentName,
                }
              : doc
          );
        }
      }
      setDocuments(newDocuments || []);
      closeIdentityModal();
    } catch (err) {
      setUploadError(err);
    }
  };

  const handleDeleteIdentity = async (): Promise<void> => {
    const { data } = await Document.deleteDocument({
      documentId: selectedDocument?.id,
      uploadType,
    });
    if (data.result === 'OK') {
      setDocuments(documents.filter((doc) => doc.documentId !== selectedDocument?.id));
      setSelectedDocument(undefined);
      setIsDeleteModalOpen(false);
    }
  };

  const addDocument = useCallback((): void => {
    setDocumentFormValues(undefined);
    setShowIdentityModal(true);
  }, []);

  const editDocument = useCallback(
    (item?: TableRow): void => {
      setDocumentFormValues(documents.find((doc) => doc.documentId === item?.id));
      setShowIdentityModal(true);
    },
    [documents]
  );
  const previewDocument = useCallback(
    (item?: TableRow): void => {
      preview(documents, item);
    },
    [documents]
  );
  const deleteDocument = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const actions = useMemo(
    () => ({
      add: addDocument,
      edit: editDocument,
      delete: deleteDocument,
      preview: previewDocument,
    }),
    [addDocument, deleteDocument, editDocument, previewDocument]
  );

  const documentTableData: TableRow[] = documents.map(
    (document: UploadDocumentFormData): TableRow => ({
      id: document.documentId,
      columns: [
        document.documentType?.label || '',
        document.createDate,
        defaultExplanation || document.explanation,
      ],
      deletable: !document.isOldDocument,
      editable: !document.isOldDocument,
      previewable: !document.isOldDocument,
    })
  );

  return (
    <>
      {showIdentityModal ? (
        <UploadDocumentModal
          defaultExplanation={defaultExplanation}
          documentTypes={documentTypes}
          error={uploadError}
          formValues={documentFormValues}
          onClose={closeIdentityModal}
          submit={handleIdentitySubmit}
        />
      ) : null}
      {isDeleteModalOpen ? (
        <Modal
          cancelText={t('general_cancel_button')}
          description={t('general_deleteRecordConfirm_text')}
          onCancel={() => setIsDeleteModalOpen((state) => !state)}
          onSubmit={handleDeleteIdentity}
          submitText={t('general_components_confirm_button')}
          title="Confirmation"
          type={ModalType.Confirmation}
        />
      ) : null}
      <TableWrapper>
        <TableContainer>
          <Table
            actionConfig={actions}
            addActionText={t('general_add_button')}
            emptyTableText={t('cardEntry_documentEntry_noUploadedDocumentFound_text')}
            headers={documentTableHeaders.map((head) => t(head))}
            items={documentTableData}
            loading={isLoading}
            onSelect={setSelectedDocument}
            pageNumber={1}
            selectedItem={selectedDocument}
            widths={['40', '30', '30']}
          />
        </TableContainer>
      </TableWrapper>
    </>
  );
};

export default UploadDocument;
